export const professionalFieldIds = [
  'fullName',
  'email',
  'phone',
  'location',
  'linkedinUrl',
  'qualification',
  'qualificationOther',
  'aboutYou',
  'hasUsGaap',
  'usGaapDetails',
  'remoteExperience',
  'remoteExperienceDetails',
  'willingRemote',
  'willingUsHours',
  'saEligible',
  'privacyAcknowledged',
] as const

export type ProfessionalFieldId = (typeof professionalFieldIds)[number]
export type ProfessionalErrors = Partial<Record<ProfessionalFieldId | 'cv', string>>

const supportedCvTypes = new Set([
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
])

const supportedCvExtensions = new Set(['pdf', 'doc', 'docx'])
const maxCvBytes = 5 * 1024 * 1024
const maxLengths: Partial<Record<ProfessionalFieldId, number>> = {
  fullName: 120,
  email: 254,
  phone: 40,
  location: 120,
  linkedinUrl: 500,
  qualificationOther: 160,
  aboutYou: 4000,
  usGaapDetails: 1000,
  remoteExperienceDetails: 1000,
}

export function isLinkedInProfileUrl(value: string) {
  try {
    const url = new URL(value)
    const hostname = url.hostname.toLowerCase().replace(/^www\./, '')
    const isLinkedInHost = hostname === 'linkedin.com' || hostname.endsWith('.linkedin.com')
    return url.protocol === 'https:' && isLinkedInHost && /^\/in\/[^/]+\/?$/i.test(url.pathname)
  } catch {
    return false
  }
}

export function validateProfessionalEnquiry(formData: FormData): ProfessionalErrors {
  const errors: ProfessionalErrors = {}
  const required: Array<[ProfessionalFieldId, string]> = [
    ['fullName', 'Enter your full name.'],
    ['email', 'Enter your personal email address.'],
    ['phone', 'Enter your mobile number.'],
    ['location', 'Enter your South African city or province.'],
    ['linkedinUrl', 'Enter your LinkedIn profile URL.'],
    ['qualification', 'Choose your qualification pathway.'],
    ['aboutYou', 'Tell us about your professional background and what you are looking for.'],
    ['hasUsGaap', 'Tell us whether you have US GAAP experience.'],
    ['remoteExperience', 'Tell us whether you have worked fully remotely before.'],
    ['willingRemote', 'Tell us whether you are willing to work fully remotely.'],
    ['willingUsHours', 'Tell us whether you are willing to work US-aligned hours if required.'],
    ['saEligible', 'Confirm whether you live in South Africa and may legally work here.'],
    ['privacyAcknowledged', 'Acknowledge the privacy notice to submit your enquiry.'],
  ]

  for (const [field, message] of required) {
    if (!String(formData.get(field) ?? '').trim()) errors[field] = message
  }

  for (const [field, maximum] of Object.entries(maxLengths) as Array<[ProfessionalFieldId, number]>) {
    const value = String(formData.get(field) ?? '').trim().replace(/\r\n/g, '\n')
    if (value.length > maximum) errors[field] = `Use ${maximum.toLocaleString('en-ZA')} characters or fewer.`
  }

  const email = String(formData.get('email') ?? '').trim()
  if (email && email.length <= 254 && !/^\S+@\S+\.\S+$/.test(email)) errors.email = 'Enter a valid email address.'

  const linkedIn = String(formData.get('linkedinUrl') ?? '').trim()
  if (linkedIn && linkedIn.length <= 500 && !isLinkedInProfileUrl(linkedIn)) {
    errors.linkedinUrl = 'Use a public LinkedIn profile URL such as https://www.linkedin.com/in/your-name.'
  }

  const aboutYou = String(formData.get('aboutYou') ?? '').trim()
  if (aboutYou && aboutYou.length < 40) {
    errors.aboutYou = 'Provide a little more detail so we can review your enquiry thoughtfully.'
  }

  if (formData.get('qualification') === 'other' && !String(formData.get('qualificationOther') ?? '').trim()) {
    errors.qualificationOther = 'Describe your qualification or professional pathway.'
  }

  if (formData.get('hasUsGaap') === 'yes' && !String(formData.get('usGaapDetails') ?? '').trim()) {
    errors.usGaapDetails = 'Briefly describe your US GAAP experience.'
  }

  if (formData.get('remoteExperience') === 'yes' && !String(formData.get('remoteExperienceDetails') ?? '').trim()) {
    errors.remoteExperienceDetails = 'Briefly describe your fully remote experience.'
  }

  const cv = formData.get('cv')
  if (cv instanceof File && cv.size > 0) {
    const extension = cv.name.split('.').pop()?.toLowerCase() ?? ''
    if (!supportedCvExtensions.has(extension) || (cv.type && !supportedCvTypes.has(cv.type))) {
      errors.cv = 'Attach a PDF, DOC or DOCX file.'
    } else if (cv.size > maxCvBytes) {
      errors.cv = 'Your CV must be 5 MB or smaller.'
    }
  }

  return errors
}
